import { Chip, CircularProgress, ClickAwayListener, Divider, Grid } from '@material-ui/core';
import { Icons, Tooltip, Typography } from '@nutrien/cxp-components';
import React, { useEffect, useMemo, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import { Deficiency, deficiencyAction, Discipline } from '../../models/models';
import { ConditionRating } from '../../rxdb/conditionRating/queryBuilder';
import useDeficiencyWithVersionComments from '../../rxdb/deficiencyComments/useDeficiencyWithVersionComments';
import { DeficiencyFilesDocument } from '../../rxdb/deficiencyFiles/queryBuilder';
import { RepairFactor } from '../../rxdb/repairFactor/queryBuilder';
import { Urgency } from '../../rxdb/urgency/queryBuilder';
import { isJestTest } from '../../test-helpers/isJestTest';
import { useFileStorage, useOnlineStatus } from '../../utilities';
import { IMAGE_PATH, XML_HEADER } from '../../utilities/constants';
import { useWindowDimensions } from '../../utilities/useWindowDimensions';
import {
  generateActionsList,
  getStatusForDeficiency,
  getTagForActiveDeficieny,
} from '../../utilities/utilityFunctions';
import DeleteDeficiency from '../DeleteDeficiency';
import StatusLabel from '../StatusLabel';
import useStyles from './CardView.styles';

interface Props {
  deficiency: Deficiency;
  onChangeForCardView: (deficiencyData: Deficiency) => void;
  setEditDeficiencyDialog: (val: boolean) => void;
  setEditFile: (val: Deficiency) => void;
  repairFactorListData: RepairFactor[];
  setOpenAuditLogsDialog: (flag: boolean) => void;
  setAuditLogsDeficiencyId: (id: number) => void;
  isEditable: boolean;
  setInspectionIdForActiveDeficiency: (id: number) => void;
  isActiveDeficiency: boolean;
  conditionRatingListData: ConditionRating[];
  urgencyListData: Urgency[];
}

const CardView: React.FC<Props> = ({
  deficiency,
  onChangeForCardView,
  setEditDeficiencyDialog,
  setEditFile,
  repairFactorListData,
  setOpenAuditLogsDialog,
  setAuditLogsDeficiencyId,
  isEditable,
  setInspectionIdForActiveDeficiency,
  isActiveDeficiency,
  urgencyListData,
  conditionRatingListData,
}) => {
  const {
    name,
    componentFailureId,
    componentType,
    remedialAction,
    repairFactorId,
    commentObservation,
    isCompletedDuringInspection,
    componentAreaInfo,
    dateObserved,
    lowerElevation,
    upperElevation,
    pkDeficiencyUUID,
    deficiencyActionedList,
    deficiencyFiles,
    assetInspectionId,
    isReInspectedComponent,
    deficiencyComponentRemoval,
    inspectorUrgency,
    inspectorConditionRating,
    componentVersionId,
    componentVersionUUID,
  } = deficiency;
  const { getAttachmentFromIndexDB, retryFileDownload } = useFileStorage();
  const online = useOnlineStatus();
  const { SiteDiscipline, CurrentUserSelected } = useMst();
  const size = useWindowDimensions();
  const isElectrical = SiteDiscipline.disciplineId === Discipline.Electrical;
  const isShaft = SiteDiscipline.disciplineId === Discipline.Shaft;
  const [imageURL, setImageURL] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [openForAssignBack, setOpenForAssignBack] = useState<boolean>(false);
  const [openForAlertInfo, setOpenForAlertInfo] = useState<boolean>(false);
  const [assignedBackComment, setAssignedBackComment] = useState<string>('');
  const [attentionFlag, setAttentionFlag] = useState<boolean>(false);
  const [actionsList, setActionsList] = useState<deficiencyAction[]>([]);
  const classes = useStyles({ imageURL });
  const [fileLoading, setFileLoading] = useState<boolean>(false);
  const conditionRating = conditionRatingListData?.find(
    item => item.sort === inspectorConditionRating,
  );
  const { selectedDeficiencyComments, selectedActiveDeficiencyComments } =
    useDeficiencyWithVersionComments(
      Number(componentFailureId),
      componentVersionId,
      isActiveDeficiency,
      CurrentUserSelected.inspectionId,
    );
  const urgency = urgencyListData?.find(item => item.sort === inspectorUrgency);
  const componentRemovalToolTip = `Component ID ${componentFailureId} to be reviewed by SC for change in 'Reason for Removal'`;

  const getS3URL = async (fileData: DeficiencyFilesDocument) => {
    let blobFile: Blob = (await getAttachmentFromIndexDB(
      isActiveDeficiency
        ? fileData?.fileComponentVersionUUId || fileData.fileAimsUUID
        : fileData.fileAimsUUID,
    )) as Blob;
    if (online && !isJestTest() && (!blobFile || blobFile.type === XML_HEADER)) {
      setFileLoading(true);
      blobFile = await retryFileDownload(
        fileData.path,
        isActiveDeficiency
          ? fileData?.fileComponentVersionUUId || fileData.fileAimsUUID
          : fileData.fileAimsUUID,
        true,
        fileData.isActiveDeficiencyFile,
      );
      setFileLoading(false);
    }
    const url = blobFile ? URL.createObjectURL(blobFile) : '';
    setImageURL(url);
    return url;
  };

  const repairFactor = useMemo(() => {
    return repairFactorListData.find(e => Number(e.id) === repairFactorId)?.name || '-';
  }, []);

  const setFile = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const { id } = e.target as HTMLInputElement;
    if (id !== 'addPhotoDeficiency' && !open) {
      setEditFile(deficiency);
      setEditDeficiencyDialog(true);
    }
  };

  const handleTooltipClose = (
    e: React.MouseEvent<Document, MouseEvent> | React.ChangeEvent<unknown>,
  ) => {
    setOpen(false);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTooltipOpen = (e: React.MouseEvent<HTMLElement>) => {
    setOpen(true);
    e.stopPropagation();
  };

  const addPhotoClick = (e: React.MouseEvent<HTMLElement>) => {
    onChangeForCardView(deficiency);
    e.preventDefault();
    e.stopPropagation();
  };

  const formatDate = (date: string) => {
    const d = new Date(date);
    const text = d.toLocaleDateString('en-US');
    return text;
  };

  const openAuditDialog = (e: { stopPropagation: () => void }) => {
    setAuditLogsDeficiencyId(Number(componentFailureId));
    setInspectionIdForActiveDeficiency(assetInspectionId);
    setOpenAuditLogsDialog(true);
    e.stopPropagation();
  };

  const handleTooltipCloseForAssignBack = (e: {
    stopPropagation: () => void;
    preventDefault: () => void;
  }) => {
    setOpenForAssignBack(false);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTooltipCloseForAlertInfo = (e: {
    stopPropagation: () => void;
    preventDefault: () => void;
  }) => {
    setOpenForAlertInfo(false);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTooltipOpenForAssignBack = (e: { stopPropagation: () => void }) => {
    setOpenForAssignBack(true);
    e.stopPropagation();
  };

  const handleTooltipOpenForAlertInfo = (e: { stopPropagation: () => void }) => {
    setOpenForAlertInfo(true);
    e.stopPropagation();
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted && !imageURL && deficiencyFiles.length > 0) {
      getS3URL(deficiencyFiles[0]);
    }
    return () => {
      isMounted = false;
    };
  }, [deficiencyFiles, online]);

  useEffect(() => {
    if (!isActiveDeficiency) {
      if (selectedDeficiencyComments?.length) {
        setAssignedBackComment(selectedDeficiencyComments[0]?.comment);
        setAttentionFlag(selectedDeficiencyComments[0]?.attentionFlag);
      }
    } else {
      if (selectedActiveDeficiencyComments?.length) {
        setAssignedBackComment(selectedActiveDeficiencyComments[0]?.comment);
        setAttentionFlag(selectedActiveDeficiencyComments[0]?.attentionFlag);
      }
    }
  }, [selectedDeficiencyComments, selectedActiveDeficiencyComments]);

  const getGridWithNameAndValue = (fieldName: string, value: string | undefined) => {
    return (
      <Grid item container xs={12} className={classes.cardViewData}>
        <Grid item xs={12} className={classes.cardViewDataGrid}>
          <Grid item xs={4}>
            <Typography variant="body1" className={classes.cardViewDataName}>
              {fieldName}
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Typography variant="body1" className={classes.cardViewDataValue}>
              {value || '-'}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  useEffect(() => {
    if (deficiencyActionedList) {
      setActionsList(generateActionsList(deficiencyActionedList));
    }
  }, [deficiencyActionedList]);

  const statusLabel = getTagForActiveDeficieny(
    deficiencyActionedList,
    deficiencyComponentRemoval,
    deficiency,
  );
  return (
    <>
      <Grid className={classes.cardViewGrid} onClick={setFile} key={componentVersionUUID}>
        <Grid className={classes.outerCard}>
          <Grid container spacing={2}>
            <Grid item container xs={12} alignItems="center">
              <Grid item xs={8}>
                <Typography variant="h5" display="inline">
                  {`Id: ${componentFailureId === '0' ? 'TBD' : componentFailureId} - ${name || ''}`}
                </Typography>
                <span className={classes.greenDot} />
                <Typography display="inline" variant="h5" className={classes.areaPcsId}>
                  {isShaft
                    ? upperElevation + ' - ' + lowerElevation
                    : componentAreaInfo[0]?.areaPcsId}
                </Typography>
                {assignedBackComment?.length ? (
                  <ClickAwayListener
                    onClickAway={e => (openForAssignBack ? handleTooltipCloseForAssignBack(e) : '')}
                  >
                    <Grid className="inlineGrid">
                      <Tooltip
                        PopperProps={{
                          disablePortal: true,
                        }}
                        onClose={e => (openForAssignBack ? handleTooltipCloseForAssignBack(e) : '')}
                        open={openForAssignBack}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title={
                          <Typography className="tooltipTextColor">
                            {assignedBackComment}
                          </Typography>
                        }
                        className={classes.inlineBlock}
                        placement="right"
                        arrow
                      >
                        <Typography
                          data-testid="asgn-back-tooltip-card"
                          onClick={e => {
                            if (openForAssignBack) handleTooltipCloseForAssignBack(e);
                            else handleTooltipOpenForAssignBack(e);
                          }}
                        >
                          <Icons.MessageSquareFeather
                            color={attentionFlag ? 'error' : 'primary'}
                            className={classes.messageIcon}
                          />
                        </Typography>
                      </Tooltip>
                    </Grid>
                  </ClickAwayListener>
                ) : null}

                {deficiencyActionedList &&
                  isReInspectedComponent &&
                  deficiencyComponentRemoval?.componentRemovalId &&
                  !deficiencyComponentRemoval?.componentRemovalIdBySC && (
                    <ClickAwayListener
                      onClickAway={e => (openForAlertInfo ? handleTooltipCloseForAlertInfo(e) : '')}
                    >
                      <Grid className="inlineGrid">
                        <Tooltip
                          PopperProps={{ disablePortal: true }}
                          onClose={e => (openForAlertInfo ? handleTooltipCloseForAlertInfo(e) : '')}
                          open={openForAlertInfo}
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                          title={
                            <Typography className="tooltipTextColor">
                              {componentRemovalToolTip}
                            </Typography>
                          }
                          className={classes.inlineBlock}
                          placement="right"
                          arrow
                        >
                          <Typography
                            data-testid="asgn-back-tooltip"
                            onClick={e => {
                              if (openForAlertInfo) handleTooltipCloseForAlertInfo(e);
                              else handleTooltipOpenForAlertInfo(e);
                            }}
                          >
                            <img
                              src={`${IMAGE_PATH}/alertCircle.svg`}
                              alt="View"
                              className={classes.alertCircle}
                            />
                          </Typography>
                        </Tooltip>
                      </Grid>
                    </ClickAwayListener>
                  )}

                {isElectrical && isCompletedDuringInspection && (
                  <Typography color="primary">
                    Completed as part of Preventative Maintenance
                  </Typography>
                )}
              </Grid>

              <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
                {deficiencyActionedList && (
                  <StatusLabel
                    taskStatusId={deficiencyActionedList.componentFailureStatus}
                    workPackageId={deficiencyActionedList.workPackageId}
                    customClass="deficiencyStatus"
                    label={getStatusForDeficiency(
                      deficiencyActionedList.componentFailureStatus || 0,
                      deficiencyActionedList.workPackageId,
                    )}
                  />
                )}
                {componentFailureId !== '0' && (
                  <Grid
                    data-testid={`audit-def-${pkDeficiencyUUID}-card`}
                    item
                    className={classes.auditLogGrid}
                    onClick={openAuditDialog}
                  >
                    <Typography
                      fontWeight={700}
                      variant="subtitle2"
                      color="primary"
                      className={classes.auditLogs}
                    >
                      Audit Log
                    </Typography>
                    <img
                      src={`${IMAGE_PATH}/customHistory.svg`}
                      alt="View"
                      className={classes.customHistory}
                    />
                  </Grid>
                )}
                {isEditable && componentFailureId !== '0' && (
                  <Divider orientation="vertical" className={classes.dividerGrid} />
                )}
                {isEditable && (
                  <>
                    <Grid item onClick={setFile}>
                      <Icons.EditFeather color="primary" />
                    </Grid>
                    {!deficiencyActionedList && (
                      <Grid
                        item
                        className={classes.deleteIcon}
                        onClick={e => {
                          setDeleteConfirm(true);
                          e.stopPropagation();
                        }}
                      >
                        <Icons.Trash2Feather color="error" />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid className={classes.cardViewBelowGrid}>
          <Grid container spacing={2}>
            <Grid item container xs={12}>
              {!isElectrical ? (
                <Grid xs={8} className={classes.cardViewInfo}>
                  {getGridWithNameAndValue('Component Type', componentType)}
                  {getGridWithNameAndValue('Remedial Action', remedialAction)}
                  {getGridWithNameAndValue('Condition Rating', conditionRating?.name)}
                  {getGridWithNameAndValue('Urgency', urgency?.name)}
                  {actionsList.some((action: deficiencyAction) => action.actionId) &&
                    actionsList.map(
                      (action: deficiencyAction) =>
                        action.actionId &&
                        getGridWithNameAndValue(
                          'ActionType/ID',
                          `${action.actionType}/${action.actionId}`,
                        ),
                    )}
                  {commentObservation?.length ? (
                    <Grid
                      item
                      container
                      xs={12}
                      className={`${classes.cardViewData} ${classes.cardViewInfoGrid}`}
                    >
                      <Grid item xs={12} className={classes.cardViewDataGrid}>
                        <Grid item xs={4}>
                          <Typography variant="body1" className={classes.cardViewDataName}>
                            Deficiency Details
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          {commentObservation?.length > 80 ? (
                            <ClickAwayListener
                              onClickAway={e => (open ? handleTooltipClose(e) : '')}
                            >
                              <div>
                                <Typography
                                  variant="body1"
                                  className={`${classes.cardViewDescription} ${classes.deficiencyDetailsCard}`}
                                >
                                  {`${`${commentObservation
                                    ?.substr(0, 80)
                                    .replace(/\s*$/, '')}...`}`}
                                </Typography>
                                <Tooltip
                                  data-testid="comment-observation-tooltip-card"
                                  PopperProps={{
                                    disablePortal: true,
                                  }}
                                  onClose={e => handleTooltipClose(e)}
                                  open={open}
                                  disableFocusListener
                                  disableHoverListener
                                  disableTouchListener
                                  title={
                                    <Typography className="tooltipTextColor">
                                      {commentObservation}
                                    </Typography>
                                  }
                                  className={classes.inlineBlock}
                                  placement="bottom"
                                  arrow
                                >
                                  <Typography
                                    color="primary"
                                    className={classes.inlineBlock}
                                    onClick={handleTooltipOpen}
                                  >
                                    Read more
                                  </Typography>
                                </Tooltip>
                              </div>
                            </ClickAwayListener>
                          ) : (
                            <Typography variant="body1" className={classes.cardViewDescription}>
                              {commentObservation?.length > 0 ? commentObservation : '-'}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : null}
                </Grid>
              ) : (
                <Grid xs={8} className={classes.cardViewInfo}>
                  {getGridWithNameAndValue(
                    'Date Observed',
                    dateObserved ? formatDate(dateObserved) : '-',
                  )}

                  {getGridWithNameAndValue('Remedial Action', remedialAction)}
                  {getGridWithNameAndValue('Repair Factor', repairFactor)}

                  {commentObservation?.length ? (
                    <Grid
                      item
                      container
                      xs={12}
                      className={`${classes.cardViewData} ${classes.cardViewInfoGrid}`}
                    >
                      <Grid item xs={12} className={classes.cardViewDataGrid}>
                        <Grid item xs={4}>
                          <Typography variant="body1" className={classes.cardViewDataName}>
                            Deficiency Details
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          {commentObservation?.length > 80 ? (
                            <ClickAwayListener
                              onClickAway={e => (open ? handleTooltipClose(e) : '')}
                            >
                              <div>
                                <Typography
                                  variant="body1"
                                  className={`${classes.cardViewDescription} ${classes.deficiencyDetailsCard}`}
                                >
                                  {`${`${commentObservation
                                    ?.substr(0, 80)
                                    .replace(/\s*$/, '')}...`}`}
                                </Typography>
                                <Tooltip
                                  PopperProps={{
                                    disablePortal: true,
                                  }}
                                  onClose={handleTooltipClose}
                                  open={open}
                                  disableFocusListener
                                  disableHoverListener
                                  disableTouchListener
                                  title={
                                    <Typography className="tooltipTextColor">
                                      {commentObservation}
                                    </Typography>
                                  }
                                  className={classes.inlineBlock}
                                  placement="bottom"
                                  arrow
                                >
                                  <Typography
                                    color="primary"
                                    className={classes.inlineBlock}
                                    onClick={handleTooltipOpen}
                                  >
                                    Read more
                                  </Typography>
                                </Tooltip>
                              </div>
                            </ClickAwayListener>
                          ) : (
                            <Typography variant="body1" className={classes.cardViewDescription}>
                              {commentObservation || '-'}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : null}
                </Grid>
              )}

              <Grid
                xs={4}
                container
                justifyContent="flex-end"
                className={
                  size.height > size.width ? classes.cardHeightGreater : classes.cardHeightLess
                }
              >
                {deficiencyFiles?.length > 0 && (
                  <Grid
                    onClick={setFile}
                    className={classes.photoCardAdd}
                    style={{
                      backgroundImage: !fileLoading ? `url("${imageURL}")` : '',
                      backgroundSize: 'cover',
                    }}
                    id={`bg-cardView-${componentFailureId}`}
                  >
                    {!fileLoading ? (
                      <div
                        className={
                          deficiencyFiles?.length > 1 ? classes.cardViewPhotoBackground : ''
                        }
                      >
                        {deficiencyFiles?.length > 1 && (
                          <div className={classes.photoDiv} style={{ flexBasis: 'min-content' }}>
                            <Typography className={classes.cardViewPhotoBackgroundFont}>
                              {`${deficiencyFiles?.length - 1}`}
                            </Typography>
                            <Typography className={classes.cardViewPhotoBackgroundFont}>
                              More
                            </Typography>
                          </div>
                        )}
                      </div>
                    ) : (
                      <CircularProgress className={classes.loaderCardView} />
                    )}
                  </Grid>
                )}
                <div
                  className={deficiencyActionedList ? classes.activeDeficiencyView : ''}
                  style={
                    statusLabel?.includes('Removed From Planning') ? { marginLeft: '-75px' } : {}
                  }
                >
                  {isEditable && (
                    <div
                      className={`${classes.photoCardAdd} ${classes.deleteIcon}`}
                      onClick={addPhotoClick}
                      data-testid={`add-photo-def-${pkDeficiencyUUID}-card`}
                    >
                      <div className={classes.photoDiv}>
                        <Icons.PlusCircleFeather color="primary" />
                        <Typography className="photoCardAddText" variant="body1">
                          Add Photo
                        </Typography>
                      </div>
                    </div>
                  )}
                  {statusLabel && (
                    <Chip
                      label={statusLabel}
                      size="medium"
                      className={
                        statusLabel.includes('Re-Inspected')
                          ? classes.reInspected
                          : classes.removedFromPlanning
                      }
                    />
                  )}
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {deleteConfirm && (
        <DeleteDeficiency
          open={deleteConfirm}
          onClose={() => setDeleteConfirm(false)}
          componentVersionUUID={componentVersionUUID}
        />
      )}
    </>
  );
};

export default CardView;
